if (!customElements.get("product-finder-quiz")) {
  customElements.define(
    "product-finder-quiz",
    class ProductFinderQuiz extends HTMLElement {
      constructor() {
        super();

        this.currentStep = 0;
        this.answers = {};
        this.implicitSelections = {};
        this.analyticsStarted = false;
        this.pathAnalyticsSent = false;
        this.completedAnalyticsSent = false;
        this.productOrder = [
          "dog_silencer_max",
          "solar_pack_max",
          "discreet_neighbor_pack",
          "bark_control_pro",
          "barkwise",
          "onguard",
          "trailblazer",
          "ultrasweep",
          "solar_safeguard",
          "pest_repeller",
          "solar_snake",
        ];
        this.productsByNeed = {
          bark: ["dog_silencer_max", "solar_pack_max", "discreet_neighbor_pack", "bark_control_pro", "barkwise", "onguard"],
          training: ["onguard", "trailblazer"],
          outdoor: ["trailblazer", "onguard"],
          pests: ["ultrasweep", "solar_safeguard", "pest_repeller", "solar_snake"],
        };
        this.questions = [
          {
            id: "need",
            type: "single",
            kicker: "Start here",
            title: "What do you need help with today?",
            helper: "Choose the main job this product should handle.",
            answers: [
              {
                label: "Stop or manage barking",
                helper: "Your neighbor's dog or your own dog at home, in the yard, or across a fence.",
                value: "bark",
                icon: "sound",
                scores: { dog_silencer_max: 2, bark_control_pro: 1, solar_pack_max: 1, discreet_neighbor_pack: 1, barkwise: 1 },
                priorityBoosts: { dog_silencer_max: 1 },
                reasons: ["You are focused on bark control, so the quiz is comparing bark devices by range, placement, and format."],
              },
              {
                label: "Fix unwanted behaviors",
                helper: "Pulling on the leash, jumping, or other habits you want to correct.",
                value: "training",
                icon: "training",
                scores: { onguard: 3 },
                reasons: ["You want support for redirecting your own dog."],
              },
              {
                label: "Prepare for walks, hikes, or travel",
                helper: "Training walks, hikes, parks, campsites, road trips, or trail days.",
                value: "outdoor",
                icon: "trail",
                scores: { trailblazer: 4, onguard: 1 },
                reasons: ["You need dog gear for outdoor time, so the quiz is comparing outdoor kits and portable support."],
              },
              {
                label: "Keep pests away",
                helper: "Vehicles, yards, gardens, garages, or rooms.",
                value: "pests",
                icon: "pest",
                reasons: ["You need a pest deterrent rather than a dog-training product."],
              },
            ],
          },
          {
            id: "situation",
            type: "multi",
            maxSelections: 2,
            maxSelectionsByNeed: {
              outdoor: 2,
              pests: 1,
            },
            kicker: "The situation",
            title: "What best describes the situation?",
            helper: "Choose up to 2 if more than one is true.",
            copyByNeed: {
              outdoor: {
                kicker: "Outdoor plans",
                title: "What kind of outdoor time are you planning for?",
                helper: "Choose one or both if they apply.",
              },
              pests: {
                kicker: "Pest problem",
                title: "Which pest problem needs the most attention?",
                helper: "Choose the main problem you want this product to solve first.",
              },
            },
            answers: [
              {
                label: "My neighbor's dog is barking",
                helper: "Barking is coming from outside your household.",
                value: "neighbor",
                icon: "neighbor",
                branches: ["bark"],
                scores: { dog_silencer_max: 5, solar_pack_max: 3, discreet_neighbor_pack: 3, bark_control_pro: 1 },
                priorityBoosts: { dog_silencer_max: 2, solar_pack_max: 1, discreet_neighbor_pack: 1 },
                reasons: ["Neighbor barking points toward range, placement, and outdoor bark-control coverage."],
              },
              {
                label: "My dog barks at home or in the yard",
                helper: "You can work directly with the dog.",
                value: "own_dog",
                icon: "home",
                branches: ["bark"],
                scores: { bark_control_pro: 4, barkwise: 3, dog_silencer_max: 1, onguard: 1 },
                priorityBoosts: { bark_control_pro: 2, barkwise: 1 },
                reasons: ["Because this is your dog, fixed-area devices, collars, and handheld training tools can all fit."],
              },
              {
                label: "I need to redirect behavior, not just barking",
                helper: "Jumping, leash pulling, chasing, or attention cues.",
                value: "general_behavior",
                icon: "training",
                branches: ["training"],
                scores: { onguard: 5, trailblazer: 2 },
                priorityBoosts: { onguard: 2 },
                reasons: ["General behavior correction points toward an in-the-moment training tool."],
              },
              {
                label: "Walks or outdoor training sessions",
                helper: "Portable support while you are moving with your dog.",
                value: "walks",
                icon: "walk",
                branches: ["training", "outdoor"],
                scores: { onguard: 4, trailblazer: 3 },
                priorityBoosts: { onguard: 2, trailblazer: 1 },
                reasons: ["Walks and training sessions favor portable, in-the-moment tools."],
              },
              {
                label: "Hikes, parks, camping, or road trips",
                helper: "Longer outings, campsites, trail days, or travel.",
                value: "dog_outings",
                icon: "trail",
                branches: ["outdoor"],
                scores: { trailblazer: 4, onguard: 1 },
                priorityBoosts: { trailblazer: 2 },
                reasons: ["Outdoor outings call for portable dog gear that is easy to bring along."],
              },
              {
                label: "Rodents are getting into a vehicle or engine bay",
                helper: "Car, truck, RV, boat, or stored vehicle.",
                value: "vehicle_pests",
                icon: "vehicle",
                branches: ["pests"],
                scores: { ultrasweep: 6 },
                priorityBoosts: { ultrasweep: 3 },
                reasons: ["Vehicle pest problems need a targeted under-hood deterrent."],
              },
              {
                label: "Pests are inside a room, garage, attic, or business",
                helper: "Indoor rodents, roaches, ants, silverfish, centipedes, or scorpions.",
                value: "indoor_pests",
                icon: "indoor",
                branches: ["pests"],
                scores: { pest_repeller: 6 },
                priorityBoosts: { pest_repeller: 3 },
                reasons: ["Indoor pest problems need a plug-in indoor repeller rather than an outdoor stake."],
              },
              {
                label: "Burrowing pests are damaging the yard or garden",
                helper: "Moles, voles, gophers, groundhogs, or similar yard pests.",
                value: "burrowing_pests",
                icon: "yard",
                branches: ["pests"],
                scores: { solar_safeguard: 6, solar_snake: 2 },
                priorityBoosts: { solar_safeguard: 3 },
                reasons: ["Burrowing yard pests point toward a solar ground-stake deterrent with broad outdoor coverage."],
              },
              {
                label: "Snakes are the main concern",
                helper: "Snake activity around a yard, garden, walkway, or perimeter.",
                value: "snake_pests",
                icon: "pest",
                branches: ["pests"],
                scores: { solar_snake: 6, solar_safeguard: 1 },
                priorityBoosts: { solar_snake: 3 },
                reasons: ["Snake concerns call for the snake-focused outdoor solar repeller."],
              },
            ],
          },
          {
            id: "location",
            type: "single",
            kicker: "Range and location",
            title: "Where will you use it most?",
            helper: "Pick the main place the recommendation needs to fit.",
            copyByNeed: {
              pests: {
                kicker: "Pest details",
                title: "Which target needs the most attention?",
                helper: "This narrows the pest products by the problem they were built for.",
              },
            },
            answers: [
              {
                label: "Indoors or a close-range spot",
                helper: "Room, doorway, garage, or nearby target area.",
                value: "indoor",
                icon: "indoor",
                branches: ["bark", "training"],
                whenNone: ["walks"],
                scores: { bark_control_pro: 4, barkwise: 2 },
                priorityBoosts: { bark_control_pro: 1 },
                reasons: ["Close-range or indoor barking points toward an entry-level area-control device."],
              },
              {
                label: "A typical yard or patio",
                helper: "Everyday outdoor coverage around the house.",
                value: "small_yard",
                icon: "yard",
                branches: ["bark", "training"],
                whenNone: ["walks"],
                scores: { bark_control_pro: 4, dog_silencer_max: 1 },
                priorityBoosts: { bark_control_pro: 1 },
                reasons: ["A typical yard does not always require the strongest long-range model."],
              },
              {
                label: "A large yard or long distance",
                helper: "Fence line, acreage, or far-away barking.",
                value: "large_yard",
                icon: "range",
                branches: ["bark"],
                scores: { dog_silencer_max: 5, solar_pack_max: 4, discreet_neighbor_pack: 2 },
                priorityBoosts: { dog_silencer_max: 1, solar_pack_max: 1 },
                reasons: ["Long distance makes range and power more important."],
              },
              {
                label: "Vehicle rodents or nesting",
                helper: "Mice, rats, squirrels, or chipmunks in an engine bay or stored vehicle.",
                value: "vehicle_rodents",
                icon: "vehicle",
                branches: ["pests"],
                whenAny: ["vehicle_pests"],
                scores: { ultrasweep: 5 },
                priorityBoosts: { ultrasweep: 2 },
                reasons: ["The target is a vehicle or engine bay."],
              },
              {
                label: "Indoor rodents, insects, or crawling pests",
                helper: "Mice, rats, roaches, ants, silverfish, centipedes, or scorpions indoors.",
                value: "indoor_rodents_insects",
                icon: "indoor",
                branches: ["pests"],
                whenAny: ["indoor_pests"],
                scores: { pest_repeller: 5 },
                priorityBoosts: { pest_repeller: 2 },
                reasons: ["The target is an indoor pest problem."],
              },
              {
                label: "Moles, voles, gophers, or groundhogs",
                helper: "Burrowing pests in a lawn, garden, or open outdoor area.",
                value: "burrowing_yard_pests",
                icon: "yard",
                branches: ["pests"],
                whenAny: ["burrowing_pests"],
                scores: { solar_safeguard: 5, solar_snake: 1 },
                priorityBoosts: { solar_safeguard: 2 },
                reasons: ["Burrowing pests need outdoor ground vibration coverage."],
              },
              {
                label: "Snakes around the yard or perimeter",
                helper: "Snake activity is the specific problem you are trying to deter.",
                value: "snakes_outdoor",
                icon: "pest",
                branches: ["pests"],
                whenAny: ["snake_pests"],
                scores: { solar_snake: 5 },
                priorityBoosts: { solar_snake: 2 },
                reasons: ["The target is snake deterrence around an outdoor perimeter."],
              },
              {
                label: "On the go with my dog",
                helper: "Walks, parks, hikes, travel, or training outside.",
                value: "dog_portable_location",
                icon: "portable",
                branches: ["training", "outdoor"],
                scores: { trailblazer: 4, onguard: 3 },
                reasons: ["Portable dog gear is important for this use case."],
              },
            ],
          },
          {
            id: "format",
            type: "single",
            kicker: "Product style",
            title: "What type of solution do you prefer?",
            helper: "Choose the format you are most likely to use consistently.",
            copyByNeed: {
              pests: {
                kicker: "Setup",
                title: "Which setup fits the space?",
                helper: "For pest control, the setup and power source are the real product differentiators.",
              },
            },
            answers: [
              {
                label: "Automatic bark-control device",
                helper: "Set it up near the barking and let it respond when needed.",
                value: "automatic",
                icon: "automatic",
                branches: ["bark"],
                scores: { bark_control_pro: 3, dog_silencer_max: 2, solar_pack_max: 2, discreet_neighbor_pack: 2 },
                priorityBoosts: { bark_control_pro: 1, dog_silencer_max: 1 },
                reasons: ["You prefer a bark-control device that can respond automatically."],
              },
              {
                label: "Handheld trainer",
                helper: "Control the timing yourself during behavior moments.",
                value: "handheld",
                icon: "handheld",
                branches: ["bark", "training", "outdoor"],
                whenAny: ["own_dog", "general_behavior", "walks"],
                whenNone: ["large_yard", "neighbor"],
                scores: { onguard: 9, trailblazer: 2 },
                priorityBoosts: { onguard: 3 },
                reasons: ["A handheld trainer gives you direct timing and control."],
              },
              {
                label: "Collar-based support",
                helper: "Wearable support for your own dog.",
                value: "collar",
                icon: "collar",
                branches: ["bark"],
                whenAny: ["own_dog"],
                whenNone: ["large_yard", "neighbor"],
                scores: { barkwise: 5 },
                priorityBoosts: { barkwise: 2 },
                reasons: ["You prefer a collar-based training format."],
              },
              {
                label: "Bundle or complete kit",
                helper: "A packaged solution with the essentials included.",
                value: "bundle",
                icon: "bundle",
                branches: ["bark", "training", "outdoor"],
                whenAny: ["neighbor", "large_yard", "walks", "dog_outings", "dog_portable_location", "outdoor"],
                scores: { trailblazer: 3, solar_pack_max: 3, discreet_neighbor_pack: 3 },
                reasons: ["A complete kit is more useful than a single item."],
              },
              {
                label: "Vehicle-mounted deterrent",
                helper: "Hardwire a dedicated device to a car, truck, boat, or RV battery.",
                value: "vehicle_mounted",
                icon: "vehicle",
                branches: ["pests"],
                whenAny: ["vehicle_rodents"],
                scores: { ultrasweep: 5 },
                priorityBoosts: { ultrasweep: 2 },
                reasons: ["A vehicle-mounted setup best fits engine bay and stored vehicle pest problems."],
              },
              {
                label: "Solar yard stake",
                helper: "Place a solar-powered stake in the ground for lawn or garden coverage.",
                value: "solar_yard_stake",
                icon: "automatic",
                branches: ["pests"],
                whenAny: ["burrowing_yard_pests"],
                scores: { solar_safeguard: 5, solar_snake: 2 },
                priorityBoosts: { solar_safeguard: 2 },
                reasons: ["A solar outdoor repeller fits yard, garden, and perimeter pest zones."],
              },
              {
                label: "Snake-focused solar stake",
                helper: "Use an outdoor solar stake specifically for snake concerns.",
                value: "solar_snake_stake",
                icon: "pest",
                branches: ["pests"],
                whenAny: ["snakes_outdoor"],
                scores: { solar_snake: 5 },
                priorityBoosts: { solar_snake: 2 },
                reasons: ["A snake-focused solar stake fits outdoor snake concerns."],
              },
              {
                label: "Plug-in indoor repeller",
                helper: "Plug into a wall outlet for rooms, garages, attics, or indoor spaces.",
                value: "plug_in",
                icon: "indoor",
                branches: ["pests"],
                whenAny: ["indoor_rodents_insects"],
                scores: { pest_repeller: 5 },
                priorityBoosts: { pest_repeller: 2 },
                reasons: ["A plug-in pest repeller fits indoor rooms, garages, and close-range spaces."],
              },
            ],
          },
          {
            id: "priority",
            type: "multi",
            maxSelections: 2,
            kicker: "Final detail",
            title: "What matters most?",
            helper: "Choose up to 2 priorities.",
            copyByNeed: {
              pests: {
                kicker: "Final pest detail",
                title: "What should the recommendation prioritize?",
                helper: "Choose up to 2 priorities for the pest setup.",
              },
            },
            answers: [
              {
                label: "Strongest coverage",
                helper: "Power, range, and confidence matter most.",
                value: "strength",
                icon: "strength",
                branches: ["bark"],
                whenNone: ["collar", "handheld"],
                scores: { dog_silencer_max: 5, solar_pack_max: 3, discreet_neighbor_pack: 2 },
                priorityBoosts: { dog_silencer_max: 3, solar_pack_max: 2 },
                reasons: ["You prioritized strongest coverage."],
              },
              {
                label: "Best value",
                helper: "A practical solution at a lower price point.",
                value: "value",
                icon: "value",
                branches: ["bark", "training", "outdoor"],
                scores: { bark_control_pro: 5, onguard: 2 },
                priorityBoosts: { bark_control_pro: 4, onguard: 1 },
                reasons: ["You prioritized value."],
              },
              {
                label: "No nearby outlet",
                helper: "The device needs outdoor power without frequent battery changes.",
                value: "solar_power",
                icon: "automatic",
                branches: ["bark"],
                whenAny: ["neighbor", "large_yard", "automatic", "bundle"],
                whenNone: ["indoor", "collar", "handheld"],
                scores: { solar_pack_max: 6, dog_silencer_max: 1 },
                priorityBoosts: { solar_pack_max: 4 },
                reasons: ["You need outdoor bark control where solar power solves the placement problem."],
              },
              {
                label: "Discreet setup",
                helper: "Low-profile placement or appearance matters.",
                value: "discreet",
                icon: "discreet",
                branches: ["bark"],
                whenAny: ["neighbor"],
                whenNone: ["collar", "handheld"],
                scores: { discreet_neighbor_pack: 12, solar_pack_max: 1 },
                priorityBoosts: { discreet_neighbor_pack: 10 },
                reasons: ["You want a discreet setup."],
              },
              {
                label: "Portable and easy to carry",
                helper: "The product should move with you.",
                value: "portable_priority",
                icon: "portable",
                branches: ["bark", "training", "outdoor"],
                whenAny: ["general_behavior", "walks", "handheld", "dog_outings", "dog_portable_location", "outdoor"],
                scores: { onguard: 3, trailblazer: 2 },
                priorityBoosts: { onguard: 1 },
                reasons: ["Portability is one of your top priorities."],
              },
              {
                label: "Vehicle protection",
                helper: "Keep rodents away from a stored vehicle or engine bay.",
                value: "vehicle_protection",
                icon: "vehicle",
                branches: ["pests"],
                whenAny: ["vehicle_rodents", "vehicle_mounted"],
                scores: { ultrasweep: 5 },
                priorityBoosts: { ultrasweep: 3 },
                reasons: ["You prioritized vehicle and engine bay protection."],
              },
              {
                label: "Solar outdoor coverage",
                helper: "Outdoor pest support without wiring.",
                value: "solar_coverage",
                icon: "automatic",
                branches: ["pests"],
                whenAny: ["burrowing_yard_pests", "solar_yard_stake"],
                scores: { solar_safeguard: 5, solar_snake: 2 },
                priorityBoosts: { solar_safeguard: 3 },
                reasons: ["You prioritized solar outdoor pest coverage."],
              },
              {
                label: "Snake deterrence",
                helper: "A focused option for snake concerns around outdoor areas.",
                value: "snake_deterrence",
                icon: "pest",
                branches: ["pests"],
                whenAny: ["snakes_outdoor", "solar_snake_stake"],
                scores: { solar_snake: 5 },
                priorityBoosts: { solar_snake: 3 },
                reasons: ["You called out outdoor pest concerns where a snake-focused deterrent may fit."],
              },
              {
                label: "Indoor plug-in support",
                helper: "Simple coverage for rooms, garages, attics, or indoor spaces.",
                value: "indoor_support",
                icon: "indoor",
                branches: ["pests"],
                whenAny: ["indoor_rodents_insects", "plug_in"],
                scores: { pest_repeller: 5 },
                priorityBoosts: { pest_repeller: 3 },
                reasons: ["You prioritized simple indoor pest support."],
              },
            ],
          },
        ];
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.nextLabel = this.dataset.nextLabel || "Next";
        this.seeResultsLabel = this.dataset.seeResultsLabel || "See results";
        this.unavailableLabel = this.dataset.unavailableLabel || "Choose options on product page";
        this.addErrorLabel = this.dataset.addErrorLabel || "Try again";
        this.panel = this.querySelector("[data-quiz-panel]");
        this.results = this.querySelector("[data-quiz-results]");
        this.resultGrid = this.querySelector("[data-quiz-result-grid]");
        this.noResults = this.querySelector("[data-quiz-no-results]");
        this.stepLabel = this.querySelector("[data-quiz-step-label]");
        this.progress = this.querySelector("[data-quiz-progress]");
        this.questionKicker = this.querySelector("[data-quiz-question-kicker]");
        this.questionTitle = this.querySelector("[data-quiz-question]");
        this.questionHelper = this.querySelector("[data-quiz-question-helper]");
        this.answersContainer = this.querySelector("[data-quiz-answers]");
        this.backButton = this.querySelector("[data-quiz-back]");
        this.nextButton = this.querySelector("[data-quiz-next]");
        this.restartButton = this.querySelector("[data-quiz-restart]");
        this.editButton = this.querySelector("[data-quiz-edit]");
        this.pathList = this.querySelector("[data-quiz-path]");
        this.resultCards = Array.from(this.querySelectorAll("[data-quiz-result-card]"));
        this.addonCards = Array.from(this.querySelectorAll("[data-quiz-addon-card]"));
        this.addons = this.querySelector("[data-quiz-addons]");

        this.backButton?.addEventListener("click", this.goBack.bind(this));
        this.nextButton?.addEventListener("click", this.goNext.bind(this));
        this.restartButton?.addEventListener("click", this.restart.bind(this));
        this.editButton?.addEventListener("click", this.editAnswers.bind(this));
        this.addEventListener("click", this.handleQuickAdd.bind(this));
        this.addEventListener("click", this.handleResultLinkClick.bind(this));

        this.renderOptionLinks();
        this.classList.add("is-ready");
        this.renderQuestion();
      }

      renderOptionLinks() {
        this.resultCards.forEach((card) => {
          const actions = card.querySelector(".product-finder-quiz__result-actions");
          const productLink = actions?.querySelector("a[href]");
          if (!actions || !productLink || actions.querySelector("[data-quiz-add-to-cart], [data-quiz-options-link]")) return;

          const optionsLink = document.createElement("a");
          optionsLink.className = "product-finder-quiz__quick-add product-finder-quiz__options-link";
          optionsLink.dataset.quizOptionsLink = "";
          optionsLink.href = productLink.href;
          optionsLink.textContent = this.unavailableLabel;
          actions.appendChild(optionsLink);
        });
      }

      renderPath(activeQuestions = this.getActiveQuestions()) {
        if (!this.pathList) return;
        this.pathList.innerHTML = "";
        activeQuestions.forEach((question, index) => {
          const item = document.createElement("li");
          item.className = "product-finder-quiz__path-item";
          item.dataset.pathIndex = String(index);
          item.innerHTML = `
            <button class="product-finder-quiz__path-button" type="button" data-path-button>
              <span class="product-finder-quiz__path-count">${index + 1}</span>
              <span>
                <span class="product-finder-quiz__path-kicker">${this.getQuestionCopy(question).kicker}</span>
                <span class="product-finder-quiz__path-answer" data-path-answer>${this.getPathSummary(question)}</span>
              </span>
            </button>
          `;
          item.querySelector("[data-path-button]")?.addEventListener("click", () => this.goToStep(index));
          this.pathList.appendChild(item);
        });
      }

      renderQuestion() {
        this.pruneAnswers();
        const activeQuestions = this.getActiveQuestions();
        if (this.currentStep >= activeQuestions.length) {
          this.currentStep = activeQuestions.length - 1;
        }
        if (this.currentStep < 0) this.currentStep = 0;

        const question = activeQuestions[this.currentStep];
        if (!question) return;
        const visibleAnswers = this.getVisibleAnswers(question);
        const selectedValues = this.getSelectedValues(question);
        const copy = this.getQuestionCopy(question);
        const isMulti = question.type === "multi";
        const maxSelections = this.getMaxSelections(question);
        const helperText = copy.helper || "";

        this.renderPath(activeQuestions);
        this.stepLabel.textContent = `Question ${this.currentStep + 1} of ${activeQuestions.length}`;
        this.progress.style.width = `${((this.currentStep + 1) / activeQuestions.length) * 100}%`;
        this.progress.parentElement?.setAttribute("aria-valuenow", String(this.currentStep + 1));
        this.progress.parentElement?.setAttribute("aria-valuemax", String(activeQuestions.length));
        this.questionKicker.textContent = copy.kicker;
        this.questionTitle.textContent = copy.title;
        this.questionHelper.textContent = isMulti && maxSelections
          ? this.withSelectionLimit(helperText, maxSelections, visibleAnswers.length)
          : helperText;
        this.answersContainer.innerHTML = "";
        this.answersContainer.dataset.selectionType = question.type;
        this.answersContainer.setAttribute("role", isMulti ? "group" : "radiogroup");
        this.answersContainer.setAttribute("aria-label", copy.title);

        visibleAnswers.forEach((answer) => {
          const selected = selectedValues.includes(answer.value);
          const selectionLimitReached = isMulti
            && maxSelections
            && selectedValues.length >= maxSelections;
          const button = document.createElement("button");
          button.type = "button";
          button.className = "product-finder-quiz__answer";
          button.dataset.answerValue = answer.value;
          button.setAttribute("aria-pressed", selected ? "true" : "false");
          button.setAttribute("role", isMulti ? "checkbox" : "radio");
          button.setAttribute("aria-checked", selected ? "true" : "false");
          button.disabled = Boolean(selectionLimitReached && !selected);
          button.innerHTML = `
            <span class="product-finder-quiz__answer-icon" aria-hidden="true">${this.getIcon(answer.icon)}</span>
            <span class="product-finder-quiz__answer-copy">
              <span class="product-finder-quiz__answer-label">${answer.label}</span>
              <span class="product-finder-quiz__answer-helper">${answer.helper}</span>
            </span>
            <span class="product-finder-quiz__answer-control" aria-hidden="true"></span>
          `;
          button.addEventListener("click", () => this.selectAnswer(question, answer));
          this.answersContainer.appendChild(button);
        });

        this.backButton.disabled = this.currentStep === 0;
        this.nextButton.disabled = selectedValues.length === 0;
        this.nextButton.querySelector("span").textContent =
          this.currentStep === activeQuestions.length - 1 ? this.seeResultsLabel : this.nextLabel;
        this.updatePathState(activeQuestions);
      }

      selectAnswer(question, answer) {
        const current = this.getSelectedAnswers(question);
        const wasSelected = current.some((item) => item.value === answer.value);
        if (question.id === "need") {
          this.implicitSelections = {};
          this.pathAnalyticsSent = false;
        } else {
          delete this.implicitSelections[question.id];
        }

        if (question.type === "multi") {
          const exists = current.some((item) => item.value === answer.value);
          if (exists) {
            this.answers[question.id] = current.filter((item) => item.value !== answer.value);
          } else {
            const maxSelections = this.getMaxSelections(question) || question.answers.length;
            if (current.length >= maxSelections) return;
            this.answers[question.id] = [...current, answer];
          }
        } else {
          this.answers[question.id] = [answer];
        }

        this.pruneAnswers();
        if (!wasSelected && question.id === "need") {
          this.trackQuizPath(answer);
        }
        this.completedAnalyticsSent = false;
        this.renderQuestion();
      }

      getQuestionCopy(question) {
        const branchCopy = question.copyByNeed?.[this.getPrimaryNeed()] || {};
        return {
          kicker: branchCopy.kicker || question.kicker,
          title: branchCopy.title || question.title,
          helper: branchCopy.helper || question.helper,
        };
      }

      withSelectionLimit(helper, maxSelections, visibleAnswerCount) {
        if (visibleAnswerCount && maxSelections >= visibleAnswerCount) {
          return helper || "Choose any that apply.";
        }

        const limitText = `Choose up to ${maxSelections}`;
        if (!helper) return `${limitText}.`;
        return helper.toLowerCase().includes("up to")
          ? helper.replace(/Choose up to \d+/i, limitText)
          : `${helper} ${limitText}.`;
      }

      getMaxSelections(question) {
        return question.maxSelectionsByNeed?.[this.getPrimaryNeed()]
          || question.maxSelections
          || 0;
      }

      getSelectedAnswers(question) {
        return Array.isArray(this.answers[question.id]) ? this.answers[question.id] : [];
      }

      getSelectedValues(question) {
        return this.getSelectedAnswers(question).map((answer) => answer.value);
      }

      getPathSummary(question) {
        const selected = this.getSelectedAnswers(question);
        if (!selected.length) return "Not answered";
        return selected.map((answer) => answer.label).join(", ");
      }

      updatePathState(activeQuestions = this.getActiveQuestions()) {
        if (!this.pathList) return;
        activeQuestions.forEach((question, index) => {
          const item = this.pathList.querySelector(`[data-path-index="${index}"]`);
          if (!item) return;
          item.classList.toggle("is-active", index === this.currentStep);
          item.classList.toggle("is-complete", this.getSelectedAnswers(question).length > 0);
          const button = item.querySelector("[data-path-button]");
          const canVisit = index <= this.currentStep || this.getSelectedAnswers(question).length > 0;
          if (button) {
            button.disabled = !canVisit;
            button.setAttribute("aria-current", index === this.currentStep ? "step" : "false");
          }
          const answer = item.querySelector("[data-path-answer]");
          if (answer) answer.textContent = this.getPathSummary(question);
        });
      }

      goToStep(index) {
        const activeQuestions = this.getActiveQuestions();
        const question = activeQuestions[index];
        if (!question) return;
        const canVisit = index <= this.currentStep || this.getSelectedAnswers(question).length > 0;
        if (!canVisit) return;

        this.results.classList.add("hidden");
        this.panel.classList.remove("hidden");
        this.currentStep = index;
        this.renderQuestion();
        this.focusCurrentQuestion();
        this.panel.scrollIntoView({ block: "start" });
      }

      goBack() {
        if (this.currentStep === 0) return;
        this.currentStep -= 1;
        this.renderQuestion();
        this.focusCurrentQuestion();
      }

      goNext() {
        const activeQuestions = this.getActiveQuestions();
        if (!this.getSelectedAnswers(activeQuestions[this.currentStep]).length) return;
        if (this.currentStep < activeQuestions.length - 1) {
          this.currentStep += 1;
          this.renderQuestion();
          this.focusCurrentQuestion();
          return;
        }
        this.showResults();
      }

      focusCurrentQuestion() {
        this.panel?.scrollIntoView({ block: "start" });
        this.questionTitle?.focus({ preventScroll: true });
      }

      showResults() {
        this.pruneAnswers();
        const recommendations = this.getRecommendations();
        this.panel.classList.add("hidden");
        this.results.classList.remove("hidden");

        this.resultCards.forEach((card) => {
          card.classList.add("hidden");
          card.classList.remove("product-finder-quiz__result-card--primary");
          card.style.removeProperty("order");
          this.renderReasonList(card, []);
        });

        const visibleRecommendations = recommendations.slice(0, 3);
        this.lastRecommendationKeys = visibleRecommendations.map((item) => item.key);
        this.noResults?.classList.toggle("hidden", visibleRecommendations.length > 0);
        this.resultGrid?.classList.toggle("hidden", visibleRecommendations.length === 0);
        this.resultGrid?.setAttribute("data-result-count", String(visibleRecommendations.length));

        visibleRecommendations.forEach((item, index) => {
          const card = this.resultCards.find((resultCard) => resultCard.dataset.resultKey === item.key);
          if (!card) return;
          card.classList.remove("hidden");
          card.classList.toggle("product-finder-quiz__result-card--primary", index === 0);
          card.style.order = String(index);
          this.prepareImages(card);
          const rank = card.querySelector("[data-result-rank]");
          if (rank) rank.textContent = index === 0 ? "Best match" : `Option ${index + 1}`;
          this.renderReasonList(card, this.getReasonsForProduct(item.key));
        });

        this.renderAddons();
        this.trackQuizComplete(visibleRecommendations);
        if (!visibleRecommendations.length) {
          this.addonCards.forEach((card) => card.classList.add("hidden"));
          this.addons?.classList.add("hidden");
        }
        this.results.scrollIntoView({ block: "start" });
      }

      getRecommendations() {
        const scores = {};
        const priority = {};
        const allowedProductKeys = this.getAllowedProductKeys();
        const selectedValues = this.getAllSelectedValues();

        Object.values(this.answers).forEach((selectedAnswers) => {
          const answers = Array.isArray(selectedAnswers) ? selectedAnswers : [];
          answers.forEach((answer) => {
            Object.entries(answer.scores || {}).forEach(([key, score]) => {
              scores[key] = (scores[key] || 0) + score;
            });
            Object.entries(answer.priorityBoosts || {}).forEach(([key, score]) => {
              priority[key] = (priority[key] || 0) + score;
            });
          });
        });

        this.applyBranchScoreAdjustments(scores, priority, selectedValues);

        return Object.entries(scores)
          .filter(([key]) => (
            (!allowedProductKeys || allowedProductKeys.has(key))
            && this.isProductEligible(key, selectedValues)
            && this.resultCards.some((card) => card.dataset.resultKey === key)
          ))
          .sort((first, second) => {
            const scoreDiff = second[1] - first[1];
            if (scoreDiff !== 0) return scoreDiff;
            const priorityDiff = (priority[second[0]] || 0) - (priority[first[0]] || 0);
            if (priorityDiff !== 0) return priorityDiff;
            return this.getProductOrderIndex(first[0]) - this.getProductOrderIndex(second[0]);
          })
          .map(([key, score]) => ({ key, score, priority: priority[key] || 0 }));
      }

      applyBranchScoreAdjustments(scores, priority, selectedValues) {
        const selected = new Set(selectedValues);

        if (this.getPrimaryNeed() === "outdoor" && selected.has("bundle") && selected.has("dog_outings")) {
          scores.trailblazer = (scores.trailblazer || 0) + 6;
          priority.trailblazer = (priority.trailblazer || 0) + 3;
        }

        if (selected.has("solar_power")) {
          scores.solar_pack_max = (scores.solar_pack_max || 0) + 9;
          priority.solar_pack_max = (priority.solar_pack_max || 0) + 4;
        }

        if (this.getPrimaryNeed() === "bark" && selected.has("strength") && (selected.has("neighbor") || selected.has("large_yard"))) {
          scores.dog_silencer_max = (scores.dog_silencer_max || 0) + 5;
          priority.dog_silencer_max = (priority.dog_silencer_max || 0) + 3;
        }

        if (selected.has("handheld")) {
          scores.onguard = (scores.onguard || 0) + 6;
          priority.onguard = (priority.onguard || 0) + 3;
        }
      }

      isProductEligible(productKey, selectedValues = this.getAllSelectedValues()) {
        const selected = new Set(selectedValues);
        const has = (...values) => values.some((value) => selected.has(value));
        const primaryNeed = this.getPrimaryNeed();

        if (primaryNeed === "pests") {
          return this.isPestProductEligible(productKey, selected);
        }

        switch (productKey) {
          case "dog_silencer_max":
            return primaryNeed === "bark"
              && !has("collar", "handheld")
              && has("neighbor", "large_yard", "strength", "solar_power");
          case "bark_control_pro":
            return primaryNeed === "bark"
              && !has("collar", "handheld")
              && !selected.has("large_yard")
              && has("own_dog", "indoor", "small_yard", "automatic", "value");
          case "solar_pack_max":
            return primaryNeed === "bark"
              && !has("indoor", "collar", "handheld")
              && has("large_yard", "solar_power");
          case "discreet_neighbor_pack":
            return primaryNeed === "bark"
              && selected.has("neighbor")
              && !has("collar", "handheld");
          case "barkwise":
            return !selected.has("large_yard")
              && (has("collar") || (selected.has("own_dog") && selected.has("indoor") && !has("handheld", "automatic")));
          case "onguard":
            return !has("collar")
              && !(primaryNeed === "bark" && selected.has("large_yard"))
              && has("general_behavior", "walks", "handheld");
          case "trailblazer":
            return !has("indoor", "collar")
              && has("outdoor", "walks", "dog_outings", "dog_portable_location", "portable_priority");
          default:
            return true;
        }
      }

      isPestProductEligible(productKey, selected) {
        const targetProducts = {
          vehicle_rodents: ["ultrasweep"],
          indoor_rodents_insects: ["pest_repeller"],
          burrowing_yard_pests: ["solar_safeguard", "solar_snake"],
          snakes_outdoor: ["solar_snake", "solar_safeguard"],
        };
        const selectedTarget = Object.keys(targetProducts).find((value) => selected.has(value));

        if (selectedTarget) {
          return targetProducts[selectedTarget].includes(productKey);
        }

        if (selected.has("vehicle_pests")) return productKey === "ultrasweep";
        if (selected.has("indoor_pests")) return productKey === "pest_repeller";
        if (selected.has("burrowing_pests")) return ["solar_safeguard", "solar_snake"].includes(productKey);
        if (selected.has("snake_pests")) return ["solar_snake", "solar_safeguard"].includes(productKey);

        return false;
      }

      getProductOrderIndex(productKey) {
        const index = this.productOrder.indexOf(productKey);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      }

      getPrimaryNeed() {
        return this.answers.need?.[0]?.value || "";
      }

      getAllowedProductKeys() {
        const productKeys = this.productsByNeed[this.getPrimaryNeed()];
        return productKeys ? new Set(productKeys) : null;
      }

      getAllSelectedValues() {
        return Object.values(this.answers)
          .flat()
          .filter(Boolean)
          .map((answer) => answer.value);
      }

      getActiveQuestions() {
        const primaryNeed = this.getPrimaryNeed();

        return this.questions.filter((question, index) => {
          if (index === 0) return true;
          if (!primaryNeed) return true;
          return this.getVisibleAnswers(question).length > 1;
        });
      }

      getVisibleAnswers(question) {
        const primaryNeed = this.getPrimaryNeed();
        const selectedValues = this.getAllSelectedValues();

        return question.answers.filter((answer) => {
          if (question.id === "need" || !primaryNeed) return true;
          if (answer.branches && !answer.branches.includes(primaryNeed)) return false;
          if (answer.whenAny && !answer.whenAny.some((value) => selectedValues.includes(value))) return false;
          if (answer.whenAll && !answer.whenAll.every((value) => selectedValues.includes(value))) return false;
          if (answer.whenNone && answer.whenNone.some((value) => selectedValues.includes(value))) return false;
          return true;
        });
      }

      pruneAnswers() {
        let changed = true;

        while (changed) {
          changed = false;

          this.questions.forEach((question) => {
            const selectedAnswers = this.getSelectedAnswers(question);
            const visibleAnswers = this.getVisibleAnswers(question);
            const visibleValues = new Set(visibleAnswers.map((answer) => answer.value));
            const filteredAnswers = selectedAnswers.filter((answer) => visibleValues.has(answer.value));

            if (filteredAnswers.length !== selectedAnswers.length) {
              changed = true;
            }

            if (question.id !== "need" && this.getPrimaryNeed() && question.type === "single" && visibleAnswers.length === 1) {
              const implicitAnswer = visibleAnswers[0];
              if (filteredAnswers[0]?.value !== implicitAnswer.value || filteredAnswers.length !== 1) {
                this.answers[question.id] = [implicitAnswer];
                this.implicitSelections[question.id] = implicitAnswer.value;
                changed = true;
              }
              return;
            }

            if (
              this.implicitSelections[question.id]
              && filteredAnswers.length === 1
              && filteredAnswers[0].value === this.implicitSelections[question.id]
            ) {
              delete this.answers[question.id];
              delete this.implicitSelections[question.id];
              changed = true;
              return;
            }

            if (filteredAnswers.length) {
              this.answers[question.id] = filteredAnswers;
              if (this.implicitSelections[question.id] && !filteredAnswers.some((answer) => answer.value === this.implicitSelections[question.id])) {
                delete this.implicitSelections[question.id];
              }
            } else if (selectedAnswers.length) {
              delete this.answers[question.id];
              delete this.implicitSelections[question.id];
            } else {
              delete this.implicitSelections[question.id];
            }
          });
        }

        const activeQuestions = this.getActiveQuestions();
        if (this.currentStep >= activeQuestions.length) {
          this.currentStep = activeQuestions.length - 1;
        }
        if (this.currentStep < 0) this.currentStep = 0;
      }

      getReasonsForProduct(productKey) {
        const reasons = [];
        Object.values(this.answers).forEach((selectedAnswers) => {
          const answers = Array.isArray(selectedAnswers) ? selectedAnswers : [];
          answers.forEach((answer) => {
            if ((answer.scores && answer.scores[productKey]) || (answer.priorityBoosts && answer.priorityBoosts[productKey])) {
              reasons.push(...(answer.reasons || []));
            }
          });
        });
        return [...new Set(reasons)].slice(0, 3);
      }

      renderReasonList(card, reasons) {
        let list = card.querySelector("[data-result-reasons]");
        if (!list) {
          list = document.createElement("ul");
          list.className = "product-finder-quiz__reason-list";
          list.dataset.resultReasons = "";
          const price = card.querySelector(".product-finder-quiz__price");
          price?.insertAdjacentElement("beforebegin", list);
        }
        list.innerHTML = reasons.map((reason) => `<li>${reason}</li>`).join("");
        list.classList.toggle("hidden", reasons.length === 0);
      }

      renderAddons() {
        const primaryNeed = this.getPrimaryNeed();
        if (primaryNeed === "pests") {
          this.addonCards.forEach((card) => card.classList.add("hidden"));
          this.addons?.classList.add("hidden");
          return;
        }

        const selectedValues = this.getAllSelectedValues();
        const addonKeys = new Set();

        if (selectedValues.some((value) => ["training", "own_dog", "walks"].includes(value))) {
          addonKeys.add("treats");
        }
        if (selectedValues.some((value) => ["outdoor", "dog_outings", "dog_portable_location", "portable_priority", "walks"].includes(value))) {
          addonKeys.add("hydropet");
          addonKeys.add("bandana");
        }

        const primaryRecommendationKey = this.lastRecommendationKeys?.[0] || "";
        if (primaryRecommendationKey === "trailblazer") {
          addonKeys.delete("treats");
          addonKeys.delete("bandana");
        }

        let visibleCount = 0;
        this.addonCards.forEach((card) => {
          const visible = addonKeys.has(card.dataset.addonKey);
          card.classList.toggle("hidden", !visible);
          if (visible) {
            this.prepareImages(card);
            visibleCount += 1;
          }
        });

        this.addons?.classList.toggle("hidden", visibleCount === 0);
      }

      prepareImages(scope) {
        scope.querySelectorAll("img[loading]").forEach((image) => {
          image.loading = "eager";
          image.setAttribute("loading", "eager");
          image.setAttribute("fetchpriority", "high");
        });
      }

      restart() {
        this.currentStep = 0;
        this.answers = {};
        this.implicitSelections = {};
        this.analyticsStarted = false;
        this.pathAnalyticsSent = false;
        this.completedAnalyticsSent = false;
        this.lastRecommendationKeys = [];
        this.noResults?.classList.add("hidden");
        this.resultGrid?.classList.remove("hidden");
        this.results.classList.add("hidden");
        this.panel.classList.remove("hidden");
        this.renderQuestion();
        this.scrollIntoView({ block: "start" });
      }

      editAnswers() {
        const activeQuestions = this.getActiveQuestions();
        const lastAnsweredIndex = activeQuestions.reduce((lastIndex, question, index) => (
          this.getSelectedAnswers(question).length ? index : lastIndex
        ), 0);
        this.goToStep(lastAnsweredIndex);
      }

      getIcon(icon) {
        const icons = {
          sound: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 10v4h3l4 4V6l-4 4H4Z" stroke-width="2" stroke-linejoin="round"/><path d="M15 9.5a4 4 0 0 1 0 5" stroke-width="2" stroke-linecap="round"/><path d="M18 7a8 8 0 0 1 0 10" stroke-width="2" stroke-linecap="round"/></svg>',
          training: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="7" y="6" width="8" height="12" rx="2" stroke-width="2"/><path d="M10 10h2M11 14h.01M17 8.5c1 .7 1.6 1.8 1.6 3S18 13.8 17 14.5M19.5 6.5c1.5 1.2 2.5 3 2.5 5s-1 3.8-2.5 5" stroke-width="1.8" stroke-linecap="round"/></svg>',
          trail: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 18 9 7l4 8 3-5 4 8H4Z" stroke-width="2" stroke-linejoin="round"/><path d="M10 18c1.5-1.7 3.3-2.5 5.5-2.5" stroke-width="2" stroke-linecap="round"/></svg>',
          gear: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke-width="2"/><path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.4 1a8 8 0 0 0-1.7-1L14.5 3h-5l-.3 3.1a8 8 0 0 0-1.7 1l-2.4-1-2 3.4L5.1 11a7 7 0 0 0 0 2l-2 1.5 2 3.4 2.4-1a8 8 0 0 0 1.7 1l.3 3.1h5l.3-3.1a8 8 0 0 0 1.7-1l2.4 1 2-3.4-2-1.5c.1-.3.1-.7.1-1Z" stroke-width="2" stroke-linejoin="round"/></svg>',
          pest: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 12c0-3 1.8-5.2 4-5.2s4 2.2 4 5.2v2.5a4 4 0 0 1-8 0V12Z" stroke-width="2"/><path d="M9.2 7.5 7 5M14.8 7.5 17 5M4.5 11.5H8M16 11.5h3.5M5.5 17.5 8.2 16M18.5 17.5 15.8 16M12 7v11" stroke-width="1.8" stroke-linecap="round"/></svg>',
          neighbor: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m3.5 11 6.5-5 6.5 5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.5 10.5V20h9v-9.5" stroke-width="2" stroke-linejoin="round"/><path d="M9 20v-4h2.5v4" stroke-width="2" stroke-linejoin="round"/><path d="M17 10.5a3.5 3.5 0 0 1 0 5" stroke-width="2" stroke-linecap="round"/><path d="M19.5 8.5a7 7 0 0 1 0 9" stroke-width="2" stroke-linecap="round"/></svg>',
          home: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m3 11 9-7 9 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 10v10h14V10" stroke-width="2" stroke-linejoin="round"/><path d="M10 20v-6h4v6" stroke-width="2" stroke-linejoin="round"/></svg>',
          walk: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="9" cy="4.8" r="2" stroke-width="2"/><path d="m8.5 7.4-1.7 4.2 3.8 1.7M7 11.4l-2.7 2.1M10.6 13.3l2.8 6M7.8 12.5l-2.6 6M10.2 8.7l2.7 2.5 4.1-1.7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.8 9.5 20 12v4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          vehicle: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 17h14l-1-6H6l-1 6Z" stroke-width="2" stroke-linejoin="round"/><path d="M7 17v2M17 17v2M8 11l1-4h6l1 4M4 14h2M18 14h2" stroke-width="2" stroke-linecap="round"/></svg>',
          yard: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 19h16M7 19V9l5-4 5 4v10" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 19v-6h6v6" stroke-width="2" stroke-linejoin="round"/></svg>',
          indoor: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 21V4h12v17M10 12h.01" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 21h18" stroke-width="2" stroke-linecap="round"/></svg>',
          range: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="3" stroke-width="2"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" stroke-width="2" stroke-linecap="round"/></svg>',
          portable: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 7V5a4 4 0 0 1 8 0v2M6 7h12l1 14H5L6 7Z" stroke-width="2" stroke-linejoin="round"/><path d="M9 11h6" stroke-width="2" stroke-linecap="round"/></svg>',
          automatic: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 6v6l4 2" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="12" r="8" stroke-width="2"/><path d="M18 4v4h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          handheld: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 4h6a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" stroke-width="2" stroke-linejoin="round"/><path d="M10 8h4M10 12h4M12 16h.01M18.5 6.5c.8.6 1.4 1.3 1.8 2.2M20.5 4.5c1.2.9 2.1 2 2.7 3.4" stroke-width="1.8" stroke-linecap="round"/></svg>',
          collar: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 9.5c1.5 4.8 4 7.2 7 7.2s5.5-2.4 7-7.2" stroke-width="2" stroke-linecap="round"/><path d="M6.5 7.5h11l1.2 3.5H5.3l1.2-3.5Z" stroke-width="2" stroke-linejoin="round"/><path d="M10.5 11v3h3v-3M8.5 7.5V5.8M15.5 7.5V5.8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          bundle: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 8h16v12H4V8Z" stroke-width="2" stroke-linejoin="round"/><path d="M8 8V6a4 4 0 0 1 8 0v2M12 8v12M4 13h16" stroke-width="2" stroke-linejoin="round"/></svg>',
          accessory: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 6h8l1 14H7L8 6Z" stroke-width="2" stroke-linejoin="round"/><path d="M10 6V4h4v2M9.5 10.5h5M10 14.5h.01M12 14.5h.01M14 14.5h.01" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          strength: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M13 2 4 14h7l-1 8 10-13h-7l0-7Z" stroke-width="2" stroke-linejoin="round"/></svg>',
          value: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2v20M16 6H9.5a3 3 0 0 0 0 6H14a3 3 0 0 1 0 6H7" stroke-width="2" stroke-linecap="round"/></svg>',
          discreet: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" stroke-width="2" stroke-linejoin="round"/><path d="m4 20 16-16" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="12" r="3" stroke-width="2"/></svg>',
          reward: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 4.5 14 9l4.8.5-3.6 3.2 1 4.8L12 15l-4.2 2.5 1-4.8-3.6-3.2L10 9l2-4.5Z" stroke-width="1.9" stroke-linejoin="round"/><path d="M18.5 6.5 20 5M5.5 6.5 4 5" stroke-width="1.7" stroke-linecap="round"/></svg>',
        };
        return icons[icon] || icons.gear;
      }

      handleQuickAdd(event) {
        const button = event.target.closest("[data-quiz-add-to-cart]");
        if (!button) return;

        const variantId = Number(button.dataset.variantId);
        if (!variantId || button.getAttribute("aria-disabled") === "true") return;
        const resultCard = button.closest("[data-quiz-result-card]");
        const resultKey = resultCard?.dataset.resultKey || "";
        const result = this.getAnalyticsResultFromCard(resultCard);

        button.setAttribute("aria-disabled", "true");
        button.classList.add("loading");
        button.classList.remove("has-error");
        const buttonLabel = button.querySelector("span");
        const defaultLabel = buttonLabel?.textContent || "";

        const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : null;
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        if (sections) {
          config.body = JSON.stringify({
            id: variantId,
            quantity: 1,
            sections,
            sections_url: window.location.pathname,
          });
        } else {
          config.body = JSON.stringify({ id: variantId, quantity: 1 });
        }

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || "Unable to add item");
            if (cart) {
              cart.skipWarranty = true;
              cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
            this.emitAnalytics("product_finder_quiz_add_to_cart", {
              result_key: resultKey,
              variant_id: variantId,
              primary_need: this.getPrimaryNeed(),
              selected_path: this.getSelectedPath(),
              result_keys: this.lastRecommendationKeys || [],
              quiz_primary_need: this.getPrimaryNeed(),
              quiz_selected_path: this.getSelectedPath(),
              quiz_result_keys: this.lastRecommendationKeys || [],
              quiz_result_key: result.key,
              quiz_result_title: result.title,
              quiz_result_url: result.url,
              quiz_result_rank: result.rank ? String(result.rank) : "",
            });
          })
          .catch((error) => {
            console.error(error);
            button.classList.add("has-error");
            if (buttonLabel) buttonLabel.textContent = this.addErrorLabel;
            window.setTimeout(() => {
              button.classList.remove("has-error");
              if (buttonLabel && defaultLabel) buttonLabel.textContent = defaultLabel;
            }, 3000);
          })
          .finally(() => {
            button.removeAttribute("aria-disabled");
            button.classList.remove("loading");
          });
      }

      handleResultLinkClick(event) {
        const link = event.target.closest("[data-quiz-result-card] a[href]");
        if (!link || link.closest("[data-quiz-addon-card]")) return;
        const resultCard = link.closest("[data-quiz-result-card]");
        const result = this.getAnalyticsResultFromCard(resultCard);
        this.emitAnalytics("product_finder_quiz_result_click", {
          result_key: resultCard?.dataset.resultKey || "",
          result_url: link.href,
          link_text: link.textContent.trim(),
          primary_need: this.getPrimaryNeed(),
          selected_path: this.getSelectedPath(),
          result_keys: this.lastRecommendationKeys || [],
          quiz_primary_need: this.getPrimaryNeed(),
          quiz_selected_path: this.getSelectedPath(),
          quiz_result_keys: this.lastRecommendationKeys || [],
          quiz_result_key: result.key,
          quiz_result_title: result.title,
          quiz_result_url: result.url || link.href,
          quiz_result_rank: result.rank ? String(result.rank) : "",
        });
      }

      trackQuizPath(answer) {
        if (!this.analyticsStarted) {
          this.analyticsStarted = true;
          this.emitAnalytics("product_finder_quiz_started", {
            primary_need: answer.value,
            quiz_primary_need: answer.value,
          });
        }

        if (this.pathAnalyticsSent) return;
        this.pathAnalyticsSent = true;
        this.emitAnalytics("product_finder_quiz_path_selected", {
          primary_need: answer.value,
          primary_need_label: answer.label,
          quiz_primary_need: answer.value,
          quiz_primary_need_label: answer.label,
        });
      }

      trackQuizComplete(recommendations) {
        if (this.completedAnalyticsSent) return;
        const results = recommendations.map((item, index) => this.getAnalyticsResult(item, index));
        const resultKeys = results.map((item) => item.key);
        const primaryResult = results[0] || {};
        const completionId = this.getQuizCompletionId();
        this.completedAnalyticsSent = true;

        this.emitAnalytics("product_finder_quiz_completed", {
          selected_path: this.getSelectedPath(),
          primary_need: this.getPrimaryNeed(),
          result_keys: resultKeys,
          primary_result_key: resultKeys[0] || "",
          result_count: resultKeys.length,
          quiz_completion_id: completionId,
          quiz_primary_need: this.getPrimaryNeed(),
          quiz_selected_path: this.getSelectedPath(),
          quiz_result_keys: resultKeys,
          quiz_primary_result_key: primaryResult.key || "",
          quiz_primary_result_title: primaryResult.title || "",
          quiz_primary_result_url: primaryResult.url || "",
          quiz_result_count: results.length,
        });

        results.forEach((result) => {
          this.emitAnalytics("product_finder_quiz_result_shown", {
            quiz_completion_id: completionId,
            quiz_primary_need: this.getPrimaryNeed(),
            quiz_selected_path: this.getSelectedPath(),
            quiz_result_key: result.key,
            quiz_result_title: result.title,
            quiz_result_url: result.url,
            quiz_result_rank: String(result.rank),
            quiz_result_is_primary: result.rank === 1 ? "true" : "false",
            result_key: result.key,
            result_url: result.url,
          });
        });
      }

      getAnalyticsResult(item, index) {
        const card = this.resultCards.find((resultCard) => resultCard.dataset.resultKey === item.key);
        const title = card?.querySelector("h3")?.textContent.trim() || item.key;
        const url = card?.querySelector("a[href]")?.href || "";

        return {
          key: item.key,
          title,
          url,
          rank: index + 1,
        };
      }

      getAnalyticsResultFromCard(card) {
        const key = card?.dataset.resultKey || "";
        const rankIndex = (this.lastRecommendationKeys || []).indexOf(key);

        return {
          key,
          title: card?.querySelector("h3")?.textContent.trim() || key,
          url: card?.querySelector("a[href]")?.href || "",
          rank: rankIndex >= 0 ? rankIndex + 1 : "",
        };
      }

      getQuizCompletionId() {
        if (window.crypto && typeof window.crypto.randomUUID === "function") {
          return window.crypto.randomUUID();
        }

        return [
          "quiz",
          Date.now(),
          Math.random().toString(36).slice(2, 10),
        ].join("-");
      }

      getSelectedPath() {
        return this.questions
          .map((question) => this.getSelectedValues(question).join("+"))
          .filter(Boolean);
      }

      emitAnalytics(eventName, payload = {}) {
        const eventPayload = Object.assign({
          event: eventName,
          event_category: "Product Finder Quiz",
          quiz_id: this.id || this.dataset.sectionId || "product-finder-quiz",
          page_path: window.location.pathname,
        }, payload);

        Object.keys(eventPayload).forEach((key) => {
          if (Array.isArray(eventPayload[key])) {
            eventPayload[key] = eventPayload[key].join(",");
          }
          if (eventPayload[key] === null || eventPayload[key] === "" || typeof eventPayload[key] === "undefined") {
            delete eventPayload[key];
          }
        });

        document.dispatchEvent(new CustomEvent("gl:product_finder_quiz", { detail: eventPayload }));
        document.dispatchEvent(new CustomEvent(eventName, { detail: eventPayload }));

        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push(Object.assign({}, eventPayload));
        }

        if (typeof window.gtag === "function") {
          const gtagPayload = Object.assign({}, eventPayload);
          delete gtagPayload.event;
          window.gtag("event", eventName, gtagPayload);
        }
      }
    }
  );
}
